/**
 * Column mapping enums and constants for flexible Excel upload
 */

// Field type options for register field mapping
export const FIELD_TYPES = {
  TEXT: 1,
  NUMBER: 2,
  DATE: 3,
  BOOLEAN: 4,
};

// Field type options for UI display
export const FIELD_TYPE_OPTIONS = [
  { text: 'Text', value: FIELD_TYPES.TEXT },
  { text: 'Number', value: FIELD_TYPES.NUMBER },
  { text: 'Date', value: FIELD_TYPES.DATE },
  { text: 'Yes/No', value: FIELD_TYPES.BOOLEAN },
];

// Standard AVA field categories
export const FIELD_CATEGORIES = {
  IDENTIFICATION: 'identification',
  HIERARCHY: 'hierarchy',
  TECHNICAL: 'technical',
  OPERATIONAL: 'operational',
  CERTIFICATION: 'certification',
  FACILITY: 'facility',
};

// Common field mappings across all tag types
export const COMMON_FIELD_MAPPINGS = {
  // Identification fields
  name: {
    category: FIELD_CATEGORIES.IDENTIFICATION,
    label: 'Tag No.',
    required: true,
    aliases: ['tag no', 'tag number', 'tag_no', 'tag_number', 'tagno', 'tagnumber', 'tag', 'equipment tag', 'item tag'],
  },
  
  // Hierarchy fields
  area: {
    category: FIELD_CATEGORIES.HIERARCHY,
    label: 'Area',
    aliases: ['area', 'plant area', 'facility area'],
  },
  unit: {
    category: FIELD_CATEGORIES.HIERARCHY,
    label: 'Unit',
    aliases: ['unit', 'process unit', 'operating unit'],
  },
  plant: {
    category: FIELD_CATEGORIES.HIERARCHY,
    label: 'Plant',
    aliases: ['plant', 'facility', 'site'],
  },
  system: {
    category: FIELD_CATEGORIES.HIERARCHY,
    label: 'System',
    aliases: ['system', 'process system'],
  },
  location: {
    category: FIELD_CATEGORIES.HIERARCHY,
    label: 'Location',
    aliases: ['location', 'physical location', 'installation location'],
  },
  
  // Facility field (special handling)
  facilityName: {
    category: FIELD_CATEGORIES.FACILITY,
    label: 'Facility Name',
    special: true,
    aliases: ['facility', 'facility name', 'site', 'site name', 'location'],
  },
};

// Equipment-specific field mappings
export const EQUIPMENT_FIELD_MAPPINGS = {
  ...COMMON_FIELD_MAPPINGS,
  
  // Technical fields
  equipmentDescription: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Equipment Description',
    aliases: ['description', 'equipment description', 'desc', 'equipment desc'],
  },
  discipline: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Discipline',
    aliases: ['discipline', 'engineering discipline'],
  },
  manufacturer: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Manufacturer',
    aliases: ['manufacturer', 'vendor', 'supplier', 'oem'],
  },
  modelNo: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Model No.',
    aliases: ['model', 'model no', 'model number', 'model_no', 'model_number'],
  },
  serialNo: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Serial No.',
    aliases: ['serial no', 'serial number', 'serial_no', 'serial_number', 'sn'],
  },
  size: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Size',
    aliases: ['size', 'equipment size', 'nominal size'],
  },
  specification: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Specification',
    aliases: ['specification', 'spec', 'technical specification'],
  },
  service: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Service',
    aliases: ['service', 'process service', 'fluid service'],
  },
  
  // Connection fields
  cableNo: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Cable No.',
    aliases: ['cable no', 'cable number', 'cable_no', 'cable_number'],
  },
  lineNo: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Line No.',
    aliases: ['line no', 'line number', 'line_no', 'line_number'],
  },
  parentTag: {
    category: FIELD_CATEGORIES.IDENTIFICATION,
    label: 'Parent Tag',
    aliases: ['parent tag', 'parent', 'master tag', 'main tag'],
  },
  
  // Certification fields
  certificateNo: {
    category: FIELD_CATEGORIES.CERTIFICATION,
    label: 'Certificate No.',
    aliases: ['certificate no', 'certificate number', 'cert no', 'cert number'],
  },
  certifyingAuthority: {
    category: FIELD_CATEGORIES.CERTIFICATION,
    label: 'Certifying Authority',
    aliases: ['certifying authority', 'certification body', 'authority'],
  },
  
  // Safety fields
  fireZone: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Fire Zone',
    aliases: ['fire zone', 'fire area', 'hazard zone'],
  },
};

// Pipeline-specific field mappings
export const PIPELINE_FIELD_MAPPINGS = {
  ...COMMON_FIELD_MAPPINGS,
  
  // Pipeline specific technical fields
  lineSize: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Line Size',
    aliases: ['line size', 'pipe size', 'nominal diameter', 'nd'],
  },
  pipeSpecification: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Pipe Specification',
    aliases: ['pipe specification', 'pipe spec', 'piping spec', 'line spec'],
  },
  subSystem: {
    category: FIELD_CATEGORIES.HIERARCHY,
    label: 'Sub System',
    aliases: ['sub system', 'subsystem', 'sub-system'],
  },
  sequenceNumber: {
    category: FIELD_CATEGORIES.IDENTIFICATION,
    label: 'Sequence Number',
    aliases: ['sequence number', 'seq no', 'seq number', 'sequence'],
  },
  
  // Fluid properties
  fluidCode: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Fluid Code',
    aliases: ['fluid code', 'service code', 'medium code'],
  },
  phase: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Phase',
    aliases: ['phase', 'fluid phase', 'service phase'],
  },
  sourService: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Sour Service',
    aliases: ['sour service', 'h2s service', 'sour'],
  },
  
  // Insulation
  insulationCode: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Insulation Code',
    aliases: ['insulation code', 'insul code', 'thermal insulation'],
  },
  insulationMm: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Insulation Mm',
    aliases: ['insulation mm', 'insulation thickness', 'insul thickness'],
  },
  
  // Routing
  lineRouting: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Line Routing',
    aliases: ['line routing', 'routing', 'pipe routing'],
  },
  
  // Operating conditions
  operatingPressure: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Operating Pressure',
    aliases: ['operating pressure', 'op pressure', 'normal pressure'],
  },
  designPressure: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Design Pressure',
    aliases: ['design pressure', 'design press', 'max pressure'],
  },
  operatingTemperature: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Operating Temperature',
    aliases: ['operating temperature', 'op temp', 'normal temp'],
  },
  designTemperature: {
    category: FIELD_CATEGORIES.OPERATIONAL,
    label: 'Design Temperature',
    aliases: ['design temperature', 'design temp', 'max temp'],
  },
  
  // Tracing
  tracingMaxTemperature: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Tracing Max Temperature',
    aliases: ['tracing max temperature', 'tracing max temp', 'trace max temp'],
  },
  tracingMaintenanceTemperature: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Tracing Maintenance Temperature',
    aliases: ['tracing maintenance temperature', 'tracing maint temp', 'trace maint temp'],
  },
};

// Structural-specific field mappings
export const STRUCTURAL_FIELD_MAPPINGS = {
  ...COMMON_FIELD_MAPPINGS,
  
  type: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Type',
    aliases: ['type', 'structural type', 'element type'],
  },
  description: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Description',
    aliases: ['description', 'desc', 'structural description'],
  },
  spec: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Spec',
    aliases: ['spec', 'specification', 'structural spec'],
  },
  weight: {
    category: FIELD_CATEGORIES.TECHNICAL,
    label: 'Weight',
    aliases: ['weight', 'mass', 'structural weight'],
  },
};
